import type React from 'react';

/**
 * One appearance contract for every page-number pagination in the builder (products grid,
 * blog posts, subcategories) — edited through the shared PaginationStyleFields panel.
 *
 * Every field is optional: an unset field yields `undefined` in the style objects below, so the
 * block keeps its own original Tailwind classes for it. Pages saved before these settings existed
 * (and blocks a merchant never touched) therefore render exactly as before.
 */
export interface PaginationAppearance {
    activeBg?: string;
    activeText?: string;
    bg?: string;
    text?: string;
    borderColor?: string;
    hoverBg?: string;
    hoverText?: string;
    radius?: number;
    size?: number;
    gap?: number;
    fontSize?: number;
}

/** Settings keys written by PaginationStyleFields (all in block.settings). */
export const PAGINATION_APPEARANCE_KEYS = [
    'paginationActiveBg', 'paginationActiveText', 'paginationBg', 'paginationText', 'paginationBorderColor',
    'paginationHoverBg', 'paginationHoverText', 'paginationRadius', 'paginationSize', 'paginationGap', 'paginationFontSize',
] as const;

const color = (value: unknown) => (typeof value === 'string' && value.trim() ? value : undefined);
const number = (value: unknown) => {
    if (value === '' || value === null || value === undefined) return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
};

export function resolvePaginationAppearance(settings: Record<string, any> | undefined | null): PaginationAppearance {
    const s = settings || {};
    // Blog posts had a two-colour version of this (paginationActiveColor / paginationColor) —
    // still honoured as a fallback so those pages keep their colours.
    const legacyActive = color(s.paginationActiveColor);
    const legacyColor = color(s.paginationColor);
    return {
        activeBg: color(s.paginationActiveBg) ?? legacyActive,
        activeText: color(s.paginationActiveText),
        bg: color(s.paginationBg),
        text: color(s.paginationText) ?? legacyColor,
        borderColor: color(s.paginationBorderColor) ?? legacyColor,
        hoverBg: color(s.paginationHoverBg),
        hoverText: color(s.paginationHoverText),
        radius: number(s.paginationRadius),
        size: number(s.paginationSize),
        gap: number(s.paginationGap),
        fontSize: number(s.paginationFontSize),
    };
}

function box(a: PaginationAppearance): React.CSSProperties {
    return {
        borderRadius: a.radius !== undefined ? a.radius : undefined,
        width: a.size !== undefined ? a.size : undefined,
        minWidth: a.size !== undefined ? a.size : undefined,
        height: a.size !== undefined ? a.size : undefined,
        fontSize: a.fontSize !== undefined ? a.fontSize : undefined,
    };
}

/** A page number (or the "x / y" pill) — `active` = the current page. */
export function paginationItemStyle(a: PaginationAppearance, active: boolean): React.CSSProperties {
    return active
        ? { ...box(a), backgroundColor: a.activeBg, color: a.activeText, borderColor: a.activeBg ?? a.borderColor }
        : { ...box(a), backgroundColor: a.bg, color: a.text, borderColor: a.borderColor };
}

/** Prev/next buttons: same look as an inactive page; width is left to the label when it has one. */
export function paginationArrowStyle(a: PaginationAppearance, withLabel = false): React.CSSProperties {
    const style = paginationItemStyle(a, false);
    if (withLabel) {
        delete style.width;
        delete style.minWidth;
    }
    return style;
}

/** Dot style: current dot = active colour, others = button background (or legacy text colour). */
export function paginationDotStyle(a: PaginationAppearance, active: boolean): React.CSSProperties {
    return { backgroundColor: active ? a.activeBg : (a.bg ?? a.text) };
}

export function paginationContainerStyle(a: PaginationAppearance): React.CSSProperties {
    return a.gap !== undefined ? { gap: a.gap } : {};
}

/**
 * Hover colours can't be inline styles (they'd also block the classes' own :hover), so they're
 * emitted as CSS scoped to the block. Elements opt in with data-pg-item; the current page carries
 * data-pg-active="true" and keeps its active colours.
 */
export function paginationHoverCss(scopeClass: string, a: PaginationAppearance): string {
    if (!a.hoverBg && !a.hoverText) return '';
    const decl = [
        a.hoverBg ? `background-color:${a.hoverBg} !important;` : '',
        a.hoverText ? `color:${a.hoverText} !important;` : '',
        a.hoverBg ? `border-color:${a.hoverBg} !important;` : '',
    ].join('');
    return `.${scopeClass} [data-pg-item]:not([data-pg-active="true"]):not(:disabled):not([aria-disabled="true"]):hover{${decl}}`;
}

export function paginationScopeClass(blockId: string | undefined): string {
    return `pg-${String(blockId || 'block').replace(/[^a-zA-Z0-9_-]/g, '')}`;
}
